/**
 * Pure decision logic for whether the MaiTeams app navbar/chrome should be shown.
 * Extracted from <ConditionalNavbar /> so it can be unit-tested without React /
 * jsdom (the repo's RTL setup can't resolve @testing-library/dom).
 */

const APP_ROOT_DOMAIN = (process.env.NEXT_PUBLIC_APP_ROOT_DOMAIN || "maiteams.com").toLowerCase();

/**
 * True when `hostname` is NOT one of the app's own hosts — i.e. a customer
 * white-label landing-page domain. Those serve a branded page (the rewritten
 * pathname is just the bare slug) and must never show MaiTeams app chrome.
 */
export function isCustomLandingPageHostname(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  const h = hostname.toLowerCase();
  const isAppHost =
    h === APP_ROOT_DOMAIN ||
    h.endsWith(`.${APP_ROOT_DOMAIN}`) ||
    h === "localhost" ||
    h.endsWith(".localhost") ||
    h === "127.0.0.1" ||
    h.endsWith(".vercel.app");
  return !isAppHost;
}

/** The pages an email recipient reaches from an unsubscribe/preferences link. */
const RECIPIENT_EMAIL_PAGE = /^\/(public\/(preferences|unsubscribe)\/|unsubscribe$|email-preferences$)/;

/** Marketing/public pages that carry their own nav. */
const MARKETING_PAGES = [
  "/",
  "/about",
  "/contact",
  "/cookies",
  "/privacy",
  "/terms",
  "/login",
  "/demo",
  "/pricing",
  "/backed-by",
];

/**
 * Whether the app navbar should be HIDDEN for the given pathname + host:
 *   - marketing pages (their own nav),
 *   - dashboard/admin/email/share routes (sidebar handles nav),
 *   - public landing-page surfaces: the flat `/p/<slug>` apex surface and the
 *     custom-domain resolver `/lp/domain/...` (Codex J2),
 *   - the public quote capability surface `/q/<token>` (Deal Engine 5e),
 *   - the email recipient's unsubscribe and preference pages (`/public/...`,
 *     and the legacy `/unsubscribe` / `/email-preferences` links),
 *   - any customer white-label host (the rewritten pathname may be the bare slug).
 */
export function shouldHideAppChrome(
  pathname: string | null | undefined,
  hostname: string | null | undefined
): boolean {
  const p = pathname || "";
  if (MARKETING_PAGES.includes(p)) return true;
  if (
    p.startsWith("/dashboard") ||
    p.startsWith("/admin") ||
    p.startsWith("/email-campaigns") ||
    p.startsWith("/share/") ||
    p.startsWith("/sage")
  ) {
    return true;
  }
  if (p.startsWith("/p/") || p.startsWith("/lp/domain")) return true;
  // Public quote capability URL (/q/<token>) — a customer-facing document
  // surface; MaiTeams app chrome has no place on it.
  if (p.startsWith("/q/")) return true;
  // An email recipient's unsubscribe / preferences pages are branded for the
  // SENDING organization (HubSpot-style); a MaiTeams "Login" bar there reads as
  // someone else's page (QA 2026-09-24 S16).
  if (RECIPIENT_EMAIL_PAGE.test(p)) return true;
  // Affiliate demo surfaces: the per-affiliate typeform /demo/<slug> and the
  // session capability URL /demo/s/<token>. MARKETING_PAGES above matches
  // "/demo" EXACTLY, so these sub-paths would otherwise render app chrome
  // around a full-screen, prospect-facing flow.
  if (p.startsWith("/demo/")) return true;
  if (isCustomLandingPageHostname(hostname)) return true;
  return false;
}
